import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Copy, Mail, Facebook, Twitter, Linkedin, Check, MessageCircle, Send } from 'lucide-react';

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  url: string;
}

const TRANSLATIONS = {
  en: {
    shareTitle: 'Share this listing',
    shareDesc: 'Choose how you want to share this listing',
    copyLink: 'Copy Link',
    copied: 'Copied!',
    email: 'Email',
    facebook: 'Facebook',
    twitter: 'X (Twitter)',
    linkedin: 'LinkedIn',
    whatsapp: 'WhatsApp',
    messenger: 'Messenger',
    telegram: 'Telegram',
    substack: 'Substack Notes',
    linkCopied: 'Link copied to clipboard',
  },
  fr: {
    shareTitle: 'Partager cette annonce',
    shareDesc: 'Choisissez comment partager cette annonce',
    copyLink: 'Copier le lien',
    copied: 'Copié!',
    email: 'Courriel',
    facebook: 'Facebook',
    twitter: 'X (Twitter)',
    linkedin: 'LinkedIn',
    whatsapp: 'WhatsApp',
    messenger: 'Messenger',
    telegram: 'Telegram',
    substack: 'Substack Notes',
    linkCopied: 'Lien copié dans le presse-papier',
  },
};

export function ShareDialog({ open, onOpenChange, title, url }: ShareDialogProps) {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const t = TRANSLATIONS[language as keyof typeof TRANSLATIONS] || TRANSLATIONS.en;

  const fullUrl = `${window.location.origin}${url}`;
  const encodedUrl = encodeURIComponent(fullUrl);
  const encodedTitle = encodeURIComponent(title);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    toast({ title: t.linkCopied });
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOptions = [
    {
      label: t.email,
      icon: Mail,
      color: 'bg-blue-600 hover:bg-blue-700',
      onClick: () => window.open(`mailto:?subject=${encodedTitle}&body=${encodedUrl}`, '_blank'),
    },
    {
      label: t.whatsapp,
      icon: MessageCircle,
      color: 'bg-[#25D366] hover:bg-[#1DA851]',
      onClick: () => window.open(`https://wa.me/?text=${encodedTitle}%20${encodedUrl}`, '_blank'),
    },
    {
      label: t.facebook,
      icon: Facebook,
      color: 'bg-[#1877F2] hover:bg-[#166FE5]',
      onClick: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, '_blank', 'width=600,height=400'),
    },
    {
      label: t.messenger,
      icon: MessageCircle,
      color: 'bg-[#0099FF] hover:bg-[#0088E0]',
      onClick: () => window.open(`https://www.facebook.com/dialog/send?link=${encodedUrl}&app_id=0&redirect_uri=${encodedUrl}`, '_blank', 'width=600,height=400'),
    },
    {
      label: t.twitter,
      icon: Twitter,
      color: 'bg-black hover:bg-gray-800',
      onClick: () => window.open(`https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`, '_blank', 'width=600,height=400'),
    },
    {
      label: t.linkedin,
      icon: Linkedin,
      color: 'bg-[#0A66C2] hover:bg-[#094D92]',
      onClick: () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, '_blank', 'width=600,height=400'),
    },
    {
      label: t.telegram,
      icon: Send,
      color: 'bg-[#0088CC] hover:bg-[#006FA8]',
      onClick: () => window.open(`https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`, '_blank', 'width=600,height=400'),
    },
    {
      label: t.substack,
      icon: Mail,
      color: 'bg-[#FF6719] hover:bg-[#E55A14]',
      onClick: () => window.open(`https://substack.com/notes?action=compose&body=${encodedTitle}%20${encodedUrl}`, '_blank'),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t.shareTitle}</DialogTitle>
          <DialogDescription>{t.shareDesc}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Copy link */}
          <div className="flex gap-2">
            <Input value={fullUrl} readOnly className="text-sm" />
            <Button variant="outline" size="icon" onClick={handleCopy}>
              {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>

          {/* Share buttons */}
          <div className="grid grid-cols-2 gap-3">
            {shareOptions.map((opt) => (
              <Button
                key={opt.label}
                className={`${opt.color} text-white`}
                onClick={opt.onClick}
              >
                <opt.icon className="mr-2 h-4 w-4" />
                {opt.label}
              </Button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
